import React, { useCallback, useMemo } from 'react';
import { inputSizes, inputVariants } from 'src/tef/components/config/variantConfig';
import { useIcon } from 'src/tef/components/hooks/useIcon';
import { useResponsiveVariant } from 'src/tef/components/hooks/useResponsiveVariant';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import { getBorderRadiusBySize } from 'src/tef/components/utils/getBorderRadius';
import { containsMaliciousPatterns, sanitizeInputMultiline } from 'src/tef/components/utils/security';
import type { ForwardedHTMLProps } from 'src/tef/components/utils/types';
import { ComponentVariant, IconProp } from 'src/tef/components/utils/types';
import { auditLogger } from 'src/utils/security';

interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>, ForwardedHTMLProps {
  variant?: ComponentVariant;
  icon?: IconProp;
  iconPosition?: 'left' | 'right';
  showClearButton?: boolean;
  onClear?: () => void;
  loading?: boolean;
  error?: boolean;
  borderless?: boolean;
  className?: string;
}

export const TextInput = React.memo(
  React.forwardRef<HTMLInputElement, TextInputProps>(
    (
      {
        variant = {},
        icon,
        iconPosition = 'left',
        showClearButton = false,
        onClear,
        loading = false,
        error = false,
        borderless = false,
        className,
        onChange,
        value,
        ...props
      },
      ref
    ) => {
      const tef = useTEF();
      const forwardedProps = forwardProps(props);
      const iconElement = useIcon(icon);

      const variantSize = variant.size || 'md';
      const responsiveSize = useResponsiveVariant(variantSize);
      const resolvedSize = typeof responsiveSize === 'string' ? responsiveSize : 'md';
      const sizeConfig = typeof resolvedSize === 'string' ? inputSizes[resolvedSize] : inputSizes.md;

      const hasLeftIcon = icon && iconPosition === 'left';
      const hasRightIcon = (icon && iconPosition === 'right') || showClearButton || loading;

      const borderRadius = getBorderRadiusBySize(resolvedSize, tef);

      const inputClasses = useMemo(() => {
        const baseClasses = cn(
          'w-full',
          sizeConfig.padding,
          sizeConfig.text,
          !borderless && borderRadius,
          !borderless && 'border',
          !borderless && (error ? inputVariants.default.error : inputVariants.default.base),
          !borderless && (error ? inputVariants.default.error : inputVariants.default.focus),
          borderless ? 'bg-transparent' : 'bg-white dark:bg-slate-800',
          tef.colors.inputText,
          'transition-all duration-200',
          'focus:outline-none',
          hasLeftIcon && sizeConfig.iconPadding,
          hasRightIcon && (inputVariants.withRightIcon.base || inputVariants.withClearButton.base),
          className
        );
        return baseClasses;
      }, [sizeConfig, error, hasLeftIcon, hasRightIcon, borderless, borderRadius, className, tef.colors.inputText]);

      const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = e.target.value;
          const sanitized = sanitizeInputMultiline(rawValue);

          if (containsMaliciousPatterns(sanitized)) {
            auditLogger.log('MALICIOUS_INPUT_BLOCKED', {
              component: 'TextInput',
              inputLength: rawValue.length,
            });
            return;
          }

          if (onChange) {
            const target = e.target as HTMLInputElement;
            const syntheticTarget = { ...target, value: sanitized, name: target.name };
            const syntheticEvent = {
              ...e,
              target: syntheticTarget,
              currentTarget: { ...e.currentTarget, value: sanitized, name: target.name },
            } as React.ChangeEvent<HTMLInputElement>;

            onChange(syntheticEvent);
          }
        },
        [onChange]
      );

      return (
        <div className="relative">
          {hasLeftIcon && (
            <div
              className={cn('absolute inset-y-0 left-0 flex items-center pointer-events-none', sizeConfig.iconOffset)}
            >
              <div className={cn(sizeConfig.icon, tef.colors.muted)}>{iconElement}</div>
            </div>
          )}

          <input
            ref={ref}
            type={props.type || 'text'}
            value={value}
            onChange={handleChange}
            className={inputClasses}
            {...forwardedProps}
            {...props}
          />

          {hasRightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {loading && (
                <div
                  className={cn(
                    sizeConfig.icon,
                    'border-2 border-current border-t-transparent rounded-full animate-spin',
                    tef.colors.muted
                  )}
                />
              )}
              {!loading && showClearButton && value && (
                <button
                  type="button"
                  onClick={onClear}
                  className={cn(
                    'p-1 rounded-md',
                    tef.colors.muted,
                    'hover:text-slate-600 dark:hover:text-slate-300 transition-colors'
                  )}
                  aria-label="Clear input"
                >
                  <svg className={sizeConfig.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {!loading && !showClearButton && icon && iconPosition === 'right' && (
                <div className={cn(sizeConfig.icon, tef.colors.muted)}>{iconElement}</div>
              )}
            </div>
          )}
        </div>
      );
    }
  )
);

TextInput.displayName = 'TextInput';
